import React, { useState, useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { getItems, getFilters, getMeta, downloadItemsExport } from "@/lib/api";
import { fmtQty, fmtMoney, fmtNum, fmtCover, fmtLt } from "@/lib/format";
import { statusMeta, actionClass } from "@/lib/statusMeta";
import { useItemDetail } from "@/lib/itemDetail";
import FilterBar from "@/components/FilterBar";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuTrigger, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Columns3, Download, Search, ChevronLeft, ChevronRight } from "lucide-react";

const money = (v) => fmtMoney(v);
export const STAGE_CLS = {
  OR: "bg-slate-100 text-slate-700", OQ: "bg-amber-50 text-amber-700", PO: "bg-sky-50 text-sky-700",
  OF: "bg-emerald-50 text-emerald-700", OX: "bg-violet-50 text-violet-700",
  OS: "bg-rose-50 text-rose-700", OJ: "bg-red-50 text-red-700",
};
export const stageClass = (t) => STAGE_CLS[t] || "bg-slate-100 text-slate-500";
const COLS = [
  { key: "item_no", label: "Item Code", sortable: true, def: true, cls: "num font-semibold text-[var(--kcsa-red-dark)]", link: true },
  { key: "desc", label: "Description", sortable: true, def: true, cls: "min-w-[180px]" },
  { key: "klass", label: "Class", sortable: true, def: true },
  { key: "subclass", label: "Subclass", sortable: true },
  { key: "warehouses_str", label: "Warehouse(s)", sortable: true, def: true, alt: "warehouse" },
  { key: "uom", label: "UOM", sortable: true, def: true },
  { key: "pref_supp", label: "Preferred Supplier", sortable: true },
  { key: "stocking_type", label: "Stocking", sortable: true },
  { key: "item_classification", label: "Commodity Class", sortable: true },
  { key: "min_qty", label: "Min", sortable: true, num: true, fmt: fmtQty },
  { key: "max_qty", label: "Max", sortable: true, num: true, fmt: fmtQty },
  { key: "consumption", label: "Avg Cons", def: true, sortable: true, num: true, fmt: fmtQty },
  { key: "soh", label: "SOH", def: true, sortable: true, num: true, fmt: fmtQty },
  { key: "soh_value", label: "SOH Value", def: true, sortable: true, num: true, fmt: money },
  { key: "open_po", label: "Open PO", sortable: true, num: true, fmt: fmtQty },
  { key: "in_receipt", label: "In Receipt", sortable: true, num: true, fmt: fmtQty },
  { key: "in_transit", label: "In Transit", sortable: true, num: true, fmt: fmtQty },
  { key: "supply_pipeline", label: "Qty on PO", def: true, sortable: true, num: true, fmt: fmtQty },
  { key: "total_supply", label: "SOH+PO", def: true, sortable: true, num: true, fmt: fmtQty },
  { key: "avg_cost", label: "Avg Cost", sortable: true, num: true, fmt: money },
  { key: "source_lt", label: "Source LT", sortable: true, num: true, fmt: fmtLt },
  { key: "manual_lt", label: "Manual LT", sortable: true, num: true, fmt: fmtLt },
  { key: "effective_lt", label: "Eff LT", def: true, sortable: true, num: true, fmt: fmtLt },
  { key: "soh_months_cover", label: "SOH Cover", def: true, sortable: true, num: true, fmt: fmtCover },
  { key: "supply_months_cover", label: "Supply Cover", sortable: true, num: true, fmt: fmtCover },
  { key: "status", label: "Status", def: true, sortable: true, status: true },
  { key: "proc_stage", label: "Proc. Stage", def: true, procStage: true },
  { key: "qty_po", label: "PO Qty", def: true, num: true, procQty: "OI" },
  { key: "qty_or", label: "OR Qty", def: true, num: true, procQty: "OR" },
  { key: "qty_oq", label: "OQ Qty", def: true, num: true, procQty: "OQ" },
  { key: "qty_of", label: "OF Qty", def: true, num: true, procQty: "OF" },
  { key: "po_required", label: "PO", def: true, sortable: true, po: true },
  { key: "recommended_po_qty", label: "Rec PO Qty", def: true, sortable: true, num: true, fmt: fmtQty },
  { key: "recommended_po_value", label: "Rec PO Value", sortable: true, num: true, fmt: money },
  { key: "recommended_action", label: "Recommended Action", def: true, sortable: true, action: true },
];

const EMPTY = { warehouse: "", class: "", subclass: "", stocking_type: "", item_classification: "", pref_supp: "", status: "" };

export default function InventoryAdvisor() {
  const [sp, setSp] = useSearchParams();
  const { openItem } = useItemDetail();
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: getMeta });
  const { data: options } = useQuery({ queryKey: ["filters"], queryFn: getFilters, enabled: !!meta?.has_data });

  const [view, setView] = useState("consolidated");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("item_no");
  const [sortDir, setSortDir] = useState("asc");
  const [searchBox, setSearchBox] = useState(sp.get("search") || "");
  const [poOnly, setPoOnly] = useState(sp.get("po") === "true");
  const [exporting, setExporting] = useState(false);
  const [visible, setVisible] = useState(() => new Set(COLS.filter((c) => c.def).map((c) => c.key)));

  const filters = {
    warehouse: sp.get("warehouse") || "", class: sp.get("class") || "", subclass: sp.get("subclass") || "",
    stocking_type: sp.get("stocking_type") || "", item_classification: sp.get("item_classification") || "",
    pref_supp: sp.get("pref_supp") || "", status: sp.get("status") || "",
  };
  const setFilter = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n); setPage(1); };
  const clearFilters = () => { const n = new URLSearchParams(sp); Object.keys(EMPTY).forEach((k) => n.delete(k)); setSp(n); setPage(1); };
  const search = sp.get("search") || "";
  const velocity = sp.get("velocity") || "";

  const params = useMemo(() => ({
    view, page, page_size: 50,
    sort: (sort === "warehouses_str" && view === "warehouse") ? "warehouse" : sort,
    sort_dir: sortDir, search: search || undefined,
    po: poOnly ? "true" : undefined, warehouse: filters.warehouse || undefined,
    class: filters.class || undefined, subclass: filters.subclass || undefined,
    stocking_type: filters.stocking_type || undefined, item_classification: filters.item_classification || undefined,
    pref_supp: filters.pref_supp || undefined, status: filters.status || undefined, velocity: velocity || undefined,
  }), [view, page, sort, sortDir, search, poOnly, velocity, filters.warehouse, filters.class, filters.subclass, filters.stocking_type, filters.item_classification, filters.pref_supp, filters.status]);

  const { data, isFetching } = useQuery({ queryKey: ["items", params], queryFn: () => getItems(params), enabled: !!meta?.has_data, placeholderData: keepPreviousData });

  const cols = COLS.filter((c) => visible.has(c.key));
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / 50));

  const toggleSort = (key) => {
    if (sort === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(key); setSortDir("desc"); }
    setPage(1);
  };
  const submitSearch = (e) => { e.preventDefault(); const n = new URLSearchParams(sp); searchBox.trim() ? n.set("search", searchBox.trim()) : n.delete("search"); setSp(n); setPage(1); };

  const doExport = async () => {
    setExporting(true);
    try { await downloadItemsExport(params); toast.success("Inventory exported to Excel"); }
    catch { toast.error("Export failed, please try again"); }
    finally { setExporting(false); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label-eyebrow">Planning</div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Inventory Advisor</h1>
        </div>
        <div className="inline-flex rounded-md border border-[var(--line)] bg-white p-0.5">
          {["consolidated", "warehouse"].map((v) => (
            <button key={v} onClick={() => { setView(v); setPage(1); }} data-testid={`view-${v}`}
              className={`px-3 h-8 rounded text-[12px] font-medium capitalize ${view === v ? "bg-[var(--kcsa-red)] text-white" : "text-slate-600"}`}>
              {v === "consolidated" ? "Consolidated Item View" : "Warehouse View"}
            </button>
          ))}
        </div>
      </div>

      <div className="kcard p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <form onSubmit={submitSearch} className="relative flex-1 min-w-[260px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={searchBox} onChange={(e) => setSearchBox(e.target.value)} data-testid="advisor-search"
              placeholder="Search Item Code(s), description, supplier…"
              className="w-full h-9 pl-9 pr-3 rounded-md border border-[var(--line)] text-[13px] outline-none focus:border-slate-400" />
          </form>
          <label className="flex items-center gap-2 text-[13px] text-slate-600">
            <Switch checked={poOnly} onCheckedChange={(v) => { setPoOnly(v); setPage(1); }} data-testid="po-only-switch" /> PO Required only
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="h-9 px-3 rounded-md border border-[var(--line)] bg-white text-[13px] font-medium text-slate-700 flex items-center gap-1.5" data-testid="columns-btn"><Columns3 className="w-4 h-4" /> Columns</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="max-h-80 overflow-y-auto slim">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              {COLS.map((c) => (
                <DropdownMenuCheckboxItem key={c.key} checked={visible.has(c.key)} onCheckedChange={() => setVisible((s) => { const n = new Set(s); n.has(c.key) ? n.delete(c.key) : n.add(c.key); return n; })} onSelect={(e) => e.preventDefault()}>
                  {c.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <button onClick={doExport} disabled={exporting} data-testid="export-btn" className="h-9 px-3 rounded-md text-white text-[13px] font-medium flex items-center gap-1.5 disabled:opacity-60" style={{ background: "var(--kcsa-red)" }}><Download className="w-4 h-4" /> {exporting ? "Exporting…" : "Export"}</button>
        </div>
        <FilterBar filters={filters} setFilter={setFilter} clearFilters={clearFilters} options={options} />
        {velocity && (
          <div className="flex items-center gap-2">
            <span className="label-eyebrow">Velocity</span>
            <span className="inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1 rounded-full bg-slate-900 text-white" data-testid="velocity-chip">
              {velocity.replace("_", " ")}
              <button onClick={() => { const n = new URLSearchParams(sp); n.delete("velocity"); setSp(n); setPage(1); }} className="hover:opacity-70" data-testid="clear-velocity">✕</button>
            </span>
          </div>
        )}
      </div>

      <div className="kcard p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--line)]">
          <span className="text-[13px] text-slate-500"><span className="num font-semibold text-slate-800">{fmtNum(total)}</span> items{isFetching ? " · loading…" : ""}</span>
          <Pager page={page} pages={pages} setPage={setPage} />
        </div>
        <div className="overflow-x-auto slim">
          <table className="w-full text-[13px]">
            <thead className="bg-[var(--surface)]">
              <tr>
                {cols.map((c) => (
                  <th key={c.key} className={`thead-cell px-3 py-2.5 text-center ${c.sortable ? "cursor-pointer select-none" : ""}`} onClick={() => c.sortable && toggleSort(c.key)}>
                    <span className="inline-flex items-center gap-1 justify-center w-full">
                      {c.label}
                      {c.sortable && sort === c.key && (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r, i) => (
                <tr key={i} className="border-t border-[var(--line)] hover:bg-slate-50">
                  {cols.map((c) => (
                    <td key={c.key} className={`px-3 py-2 text-center ${c.num ? "num" : ""} ${c.cls || ""} text-slate-700`}>
                      <Cell col={c} row={r} openItem={openItem} />
                    </td>
                  ))}
                </tr>
              ))}
              {data && data.rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-slate-400">No items match the current filters.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-end px-4 py-2 border-t border-[var(--line)]">
          <Pager page={page} pages={pages} setPage={setPage} />
        </div>
      </div>
    </div>
  );
}

function Cell({ col, row, openItem }) {
  const v = row[col.key] ?? (col.alt ? row[col.alt] : undefined);
  if (col.link) return <button className="hover:underline" onClick={() => openItem(row.item_no)} data-testid={`item-link-${row.item_no}`}>{v}</button>;
  if (col.status) { const s = statusMeta(v); return <Badge variant="outline" className={`${s.badge} rounded-full text-[11px]`}>{s.label}</Badge>; }
  if (col.po) return <span className={`text-[11px] font-semibold ${v ? "text-red-600" : "text-slate-400"}`}>{v ? "YES" : "NO"}</span>;
  if (col.procStage) {
    const p = row.proc;
    if (!p || !p.primary_token) return <span className="text-slate-300">–</span>;
    const extra = (p.stage_tokens || []).length - 1;
    return (
      <span title={p.primary_label} data-testid={`proc-stage-${row.item_no}`} className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${stageClass(p.primary_token)}`}>
        {p.primary_token}{extra > 0 ? ` +${extra}` : ""}
      </span>
    );
  }
  if (col.procQty) {
    const st = (row.proc?.stages || []).find((s) => s.code === col.procQty);
    return st && st.qty ? fmtQty(st.qty) : <span className="text-slate-300">–</span>;
  }
  if (col.action) return <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded whitespace-nowrap ${actionClass(v)}`}>{v}</span>;
  if (col.fmt) return col.fmt(v);
  return v ?? "–";
}

function Pager({ page, pages, setPage }) {
  return (
    <div className="flex items-center gap-2 text-[12px] text-slate-600">
      <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="h-7 w-7 grid place-items-center rounded border border-[var(--line)] disabled:opacity-40" data-testid="prev-page"><ChevronLeft className="w-4 h-4" /></button>
      <span className="num">{page} / {pages}</span>
      <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="h-7 w-7 grid place-items-center rounded border border-[var(--line)] disabled:opacity-40" data-testid="next-page"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );
}
